import { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Form, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import SelectSearchInput from '../../SelectSearch';

const ViewUnitDefinitions = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [unitDefinitions, setUnitDefinitions] = useState([]);
    const [token, setToken] = useState(sessionStorage.getItem('token') || "");
    const [editingId, setEditingId] = useState(null);
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [usageData, setUsageData] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        rate: '',
        unit: '',
        uom: ''
    });

    useEffect(() => {
        fetchUnitDefinitions();
    }, [token, selectedOutlet]);

    const fetchUnitDefinitions = async () => {
        if (!token && !selectedOutlet) return;

        try {
            const response = await axios.get(`${baseUrl}/unit-definitions`, {
                params: {
                    token,
                    outlet: selectedOutlet
                }
            });
            setUnitDefinitions(response.data);
        } catch (error) {
            console.error('Error fetching unit definitions:', error);
            toast.error('Error fetching unit definitions');
        }
    };

    const fetchUsageData = async (id) => {
        const item = unitDefinitions.find(i => i.id === id);

        if (!item) {
            toast.error("Item not found");
            return;
        }

        try {
            const response = await axios.post(`${baseUrl}/unit-definition/check-usage`, {
                token: token,
                outlet: selectedOutlet,
                name: item.name
            });
            const data = response.data;
            setUsageData(data);
        } catch (error) {
            toast.error("Failed to fetch usage data");
            console.log("Usage data", error);
        }
    };

    const handleEditClick = async (unit) => {
        setEditingId(unit.id);
        setEditFormData({
            name: unit.name,
            rate: unit.rate,
            unit: unit.unit,
            uom: unit.uom
        });
        await fetchUsageData(unit.id);
    };

    const handleCancelClick = () => {
        setEditingId(null);
        setUsageData(null);
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData({
            ...editFormData,
            [name]: value
        });
    };

    const showSaveConfirmation = (unitId) => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save these changes?</p>
                <div className="d-flex justify-content-between gap-2">
                    <button
                        className="px-4 py-2 bg-success text-white rounded hover-bg-success"
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                await axios.put(`${baseUrl}/unit-definition/${unitId}`, {
                                    ...editFormData,
                                    token,
                                    outlet: selectedOutlet
                                });
                                toast.success("Unit definition updated successfully");
                                fetchUsageData(unitId);
                                fetchUnitDefinitions();
                                setEditingId(null);
                            } catch (error) {
                                console.error('Error updating unit definition:', error);
                                toast.error('Error updating unit definition');
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-secondary text-white rounded hover-bg-secondary"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-confirmation'
            }
        );
    };

    const showDeleteConfirmation = async (unit) => {
        try {
            // Fetch usage data first
            const item = unitDefinitions.find(i => i.id === unit.id);
            if (!item) {
                toast.error("Item not found");
                return;
            }

            const response = await axios.post(`${baseUrl}/unit-definition/check-usage`, {
                token: token,
                outlet: selectedOutlet,
                name: item.name
            });
            const usageData = response.data;

            // Now show the confirmation with the fetched data
            toast.info(
                <div className="p-2">
                    <p className="mb-3 text-white">Are you sure you want to delete "{unit.name}"?</p>

                    {!usageData.used_in_recipe && !usageData.used_in_sub_recipe ? (
                        <Alert variant="info" className="p-2">
                            This ingredient is not used in any recipes or sub-recipes.
                        </Alert>
                    ) : (
                        <Alert variant="warning" className="p-2">
                            <p>This ingredient is used in:</p>
                            {usageData.used_in_recipe && (
                                <>
                                    <strong>Recipes:</strong>
                                    <ul>
                                        {usageData.recipes.map((recipe, index) => (
                                            <li key={index}>{recipe}</li>
                                        ))}
                                    </ul>
                                </>
                            )}
                            {usageData.used_in_sub_recipe && (
                                <>
                                    <strong>Sub-Recipes:</strong>
                                    <ul>
                                        {usageData.sub_recipes.map((subRecipe, index) => (
                                            <li key={index}>{subRecipe}</li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </Alert>
                    )}

                    <div className="flex justify-between gap-2">
                        <button
                            className="px-4 py-2 bg-danger text-white rounded hover-bg-danger"
                            onClick={async () => {
                                toast.dismiss();
                                try {
                                    await axios.delete(`${baseUrl}/unit-definition/${unit.id}`, {
                                        data: {
                                            token,
                                            outlet: selectedOutlet
                                        }
                                    });
                                    fetchUnitDefinitions();
                                    toast.success("Unit definition deleted successfully");
                                } catch (error) {
                                    console.error('Error deleting unit definition:', error);
                                    toast.error('Error deleting unit definition');
                                }
                            }}
                        >
                            Delete
                        </button>
                        <button
                            className="px-4 py-2 bg-secondary text-white rounded hover-bg-secondary"
                            onClick={() => toast.dismiss()}
                        >
                            Cancel
                        </button>
                    </div>
                </div>,
                {
                    autoClose: false,
                    closeButton: false,
                    toastId: 'delete-confirmation'
                }
            );
        } catch (error) {
            toast.error("Failed to fetch usage data");
            console.log("Error fetching usage data", error);
        }
    };

    const renderUsageInfo = (unitId) => {
        if (!usageData || editingId !== unitId) return null;

        if (!usageData.used_in_recipe && !usageData.used_in_sub_recipe) {
            return (
                <Alert variant="info" className="mt-2">
                    This ingredient is not used in any recipes or sub-recipes.
                </Alert>
            );
        }

        return (
            <Alert variant="warning" className="mt-2">
                <p>This ingredient is used in:</p>
                {usageData.used_in_recipe && (
                    <>
                        <strong>Recipes:</strong>
                        <ul>
                            {usageData.recipes.map((recipe, index) => (
                                <li key={index}>{recipe}</li>
                            ))}
                        </ul>
                    </>
                )}
                {usageData.used_in_sub_recipe && (
                    <>
                        <strong>Sub-Recipes:</strong>
                        <ul>
                            {usageData.sub_recipes.map((subRecipe, index) => (
                                <li key={index}>{subRecipe}</li>
                            ))}
                        </ul>
                    </>
                )}
            </Alert>
        );
    };

    return (
        <div className='min-h-screen flex flex-col'>
            <Navbar />

            <div className='flex-grow'>
                <div className="max-w-5xl mx-auto mt-5 text-sm">
                    <div className="flex justify-center text-center mb-4">
                        <SelectSearchInput
                            token={token}
                            setToken={setToken}
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                        />
                    </div>

                    <div className='mb-3 text-xl font-semibold'>Unit Definitions</div>

                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Unit</th>
                                <th>UOM</th>
                                <th>Rate</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {unitDefinitions.map((unit) => (
                                <>
                                    <tr key={unit.id}>
                                        <td>{unit.name}</td>
                                        <td>
                                            {editingId === unit.id ? (
                                                <Form.Control
                                                    type="number"
                                                    name="unit"
                                                    value={editFormData.unit}
                                                    onChange={handleEditFormChange}
                                                    min="0"
                                                    style={{ width: '120px' }}
                                                />
                                            ) : (
                                                unit.unit
                                            )}
                                        </td>
                                        <td>
                                            {editingId === unit.id ? (
                                                <Form.Control
                                                    type="text"
                                                    name="uom"
                                                    value={editFormData.uom}
                                                    onChange={handleEditFormChange}
                                                    style={{ width: '120px' }}
                                                />
                                            ) : (
                                                unit.uom
                                            )}
                                        </td>
                                        <td>
                                            {editingId === unit.id ? (
                                                <Form.Control
                                                    type="number"
                                                    name="rate"
                                                    value={editFormData.rate}
                                                    onChange={handleEditFormChange}
                                                    min="0"
                                                    style={{ width: '120px' }}
                                                />
                                            ) : (
                                                unit.rate
                                            )}
                                        </td>
                                        <td className='whitespace-nowrap'>
                                            {editingId === unit.id ? (
                                                <>
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={() => showSaveConfirmation(unit.id)}
                                                        className="me-2"
                                                    >
                                                        <FaCheck />
                                                    </Button>
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={handleCancelClick}
                                                    >
                                                        <FaTimes />
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        onClick={() => handleEditClick(unit)}
                                                        className="me-2"
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => showDeleteConfirmation(unit)}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                    {renderUsageInfo(unit.id)}
                                </>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ViewUnitDefinitions;